'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '../../lib/utils'
import type { LucideIcon } from 'lucide-react'

/**
 * DrawerTabs - Enhanced tabs component designed for use in drawers/sheets
 * Features: Icon support, count badges, primary color active state, drawer-optimized sizing
 *
 * Use with BaseDrawer's stickyContent prop to keep tabs fixed while content scrolls:
 *
 * <BaseDrawer
 *   stickyContent={
 *     <DrawerTabs value={tab} onValueChange={setTab}>
 *       <DrawerTabsList>
 *         <DrawerTabsTrigger value="overview" icon={Layers}>Overview</DrawerTabsTrigger>
 *       </DrawerTabsList>
 *     </DrawerTabs>
 *   }
 * >
 *   {tab === 'overview' && <OverviewContent />}
 * </BaseDrawer>
 */

// Context to share the current active tab value
const DrawerTabsContext = React.createContext<string | undefined>(undefined)

interface DrawerTabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  /** Additional className for the wrapper */
  className?: string
}

function DrawerTabs({
  className,
  children,
  value,
  onValueChange,
  defaultValue,
  ...props
}: DrawerTabsProps) {
  return (
    <DrawerTabsContext.Provider value={value}>
      <div className={cn("px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-b border-border/20", className)}>
        <TabsPrimitive.Root
          data-slot="drawer-tabs"
          className="flex flex-col"
          value={value}
          onValueChange={onValueChange}
          defaultValue={defaultValue}
          {...props}
        >
          {children}
        </TabsPrimitive.Root>
      </div>
    </DrawerTabsContext.Provider>
  )
}

interface DrawerTabsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {}

function DrawerTabsList({
  className,
  ...props
}: DrawerTabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="drawer-tabs-list"
      className={cn(
        // Exact match to PageTabsNav variant="card"
        'glass-card rounded-2xl overflow-hidden border border-border/50 flex',
        className,
      )}
      {...props}
    />
  )
}

interface DrawerTabsTriggerProps extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
  /** Optional icon to display before the label */
  icon?: LucideIcon
  /** The value that identifies this tab - required for active state detection */
  value: string
}

function DrawerTabsTrigger({
  className,
  children,
  icon: Icon,
  value,
  ...props
}: DrawerTabsTriggerProps) {
  const activeValue = React.useContext(DrawerTabsContext)
  const isActive = value === activeValue

  return (
    <TabsPrimitive.Trigger
      data-slot="drawer-tabs-trigger"
      value={value}
      className={cn(
        // Base styles
        'flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative overflow-hidden',
        'transition-all duration-200',
        // Default state  
        'text-muted-foreground hover:text-foreground hover:bg-secondary/50',
        // Focus states
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset',
        // Disabled
        'disabled:pointer-events-none disabled:opacity-50',
        // Active state
        isActive && 'bg-primary/15 text-primary font-semibold',
        className,
      )}
      {...props}
    >
      {Icon && <Icon className={cn("w-4 h-4", isActive && "text-primary")} />}
      {children}
      {/* Bottom indicator - only render when active */}
      {isActive && (
        <span 
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full"
          aria-hidden="true"
        />
      )}
    </TabsPrimitive.Trigger>
  )
}

interface DrawerTabsContentProps extends React.ComponentProps<typeof TabsPrimitive.Content> {}

function DrawerTabsContent({
  className,
  ...props
}: DrawerTabsContentProps) {
  return (
    <TabsPrimitive.Content
      data-slot="drawer-tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export {
  DrawerTabs,
  DrawerTabsList,
  DrawerTabsTrigger,
  DrawerTabsContent,
  type DrawerTabsProps,
  type DrawerTabsListProps,
  type DrawerTabsTriggerProps,
  type DrawerTabsContentProps,
}
